'use client';

import ButtonClose from '@components/button-close/ButtonClose';
import LoaderCircle from '@components/loader-circle/LoaderCircle';
import PanoramaCompass from '@components/panorama-modal/components/PanoramaCompass';
import Gradient_4 from '@images/gradients/gradient_4.webp';
import PanoramaEvening from '@images/panorama/1_full.webp';
import PanoramaMorning from '@images/panorama/2_full.webp';
import PanoramaAfternoon from '@images/panorama/3_full.webp';
import { handlePanoramaModel, handlepanoramaImgsLoading, pamoramasSelector, panoramaModelSelector } from '@lib/store/slices/ComponentsSlice';
import { changeActivePanorama } from '@lib/store/slices/ProjectSlice';
import { preloadImages } from '@lib/tools/_preload-images';
import { useViewport } from '@lib/tools/hooks/useViewport';
import { animated, useSpring } from '@react-spring/web';
import classNames from 'classnames';
import NextImage from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import style from './PanoramaModal.module.scss';
function PanoramaModal() {
  const [isOnline, setIsOnline] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [panoramaY, setPanoramaY] = useState([0, 0, 0]);
  const [loadingError, setLoadingError] = useState(null);
  const [toggle, setToggle] = useState(true);
  const viewersRef = useRef([]); // Stores viewer references for cleanup
  const panoramaRef = useRef();
  const panoramaRefTwo = useRef();
  const panoramaRefThree = useRef();
  const dispatch = useDispatch();
  const pamoramasImgesLoading = useSelector(pamoramasSelector);
  const forBtns = useSelector(state => state.projectinfo.panoramas);
  const openPanoramaModel = useSelector(panoramaModelSelector);
  const {
    isMobile,
    viewport
  } = useViewport();
  const opacityStyle = useSpring({
    config: {
      duration: 800
    },
    opacity: toggle ? 0 : 1,
    delay: 0
  });
  const changePic = item => {
    dispatch(changeActivePanorama(item.id));
    switch (item.id) {
      case 0:
        panoramaRef.current.style.opacity = 1;
        panoramaRefTwo.current.style.opacity = 0;
        panoramaRefThree.current.style.opacity = 0;
        panoramaRef.current.style.zIndex = 12;
        panoramaRefTwo.current.style.zIndex = 0;
        panoramaRefThree.current.style.zIndex = 0;
        break;
      case 1:
        panoramaRef.current.style.opacity = 0;
        panoramaRefTwo.current.style.opacity = 1;
        panoramaRefThree.current.style.opacity = 0;
        panoramaRef.current.style.zIndex = 0;
        panoramaRefTwo.current.style.zIndex = 12;
        panoramaRefThree.current.style.zIndex = 0;
        break;
      case 2:
        panoramaRef.current.style.opacity = 0;
        panoramaRefTwo.current.style.opacity = 0;
        panoramaRefThree.current.style.opacity = 1;
        panoramaRef.current.style.zIndex = 0;
        panoramaRefTwo.current.style.zIndex = 0;
        panoramaRefThree.current.style.zIndex = 12;
        break;
      default:
        break;
    }
  };
  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      setIsOnline(navigator.onLine);
    }
  }, []);
  useEffect(() => {
    if (!openPanoramaModel || !viewport) {
      return undefined;
    }
    let isMounted = true;
    let intervalId;
    async function initPanorama() {
      if (!panoramaRef.current) return;
      try {
        // Dynamically import marzipano (no three.js needed!)
        const MarzipanoModule = await import('marzipano');
        const Marzipano = MarzipanoModule.default || MarzipanoModule;
        if (!isMounted) return;
        dispatch(changeActivePanorama(0));
        setIsLoading(false);

        // Initialize 3 separate viewers to match the existing DOM architecture
        const viewerOpts = {
          controls: {
            mouseViewMode: 'drag'
          }
        };
        const viewer_1 = new Marzipano.Viewer(panoramaRef.current, viewerOpts);
        const viewer_2 = new Marzipano.Viewer(panoramaRefTwo.current, viewerOpts);
        const viewer_3 = new Marzipano.Viewer(panoramaRefThree.current, viewerOpts);
        viewersRef.current = [viewer_1, viewer_2, viewer_3];

        // Disable zoom to match OrbitControls.noZoom = true
        viewer_1.controls().disableMethod('scrollZoom');
        viewer_2.controls().disableMethod('scrollZoom');
        viewer_3.controls().disableMethod('scrollZoom');

        // Setup geometries and views
        // Assuming equirectangular format. Marzipano usually requires width, 8192 is a safe fallback
        const geometry = new Marzipano.EquirectGeometry([{
          width: 8192
        }]);
        const createSceneParams = () => ({
          geometry,
          view: new Marzipano.RectilinearView({
            yaw: 0,
            pitch: 0,
            fov: Math.PI / 2
          })
        });
        const scene_1 = viewer_1.createScene({
          source: Marzipano.ImageUrlSource.fromString(forBtns[0].imgMob.src),
          ...createSceneParams()
        });
        const scene_2 = viewer_2.createScene({
          source: Marzipano.ImageUrlSource.fromString(forBtns[1].imgMob.src),
          ...createSceneParams()
        });
        const scene_3 = viewer_3.createScene({
          source: Marzipano.ImageUrlSource.fromString(forBtns[2].imgMob.src),
          ...createSceneParams()
        });
        scene_1.switchTo();
        scene_2.switchTo();
        scene_3.switchTo();
        const scenes = [scene_1, scene_2, scene_3];

        // Coordinate interval (Marzipano uses yaw in radians natively)
        intervalId = setInterval(() => {
          if (!isMounted) return;
          const radToDeg = rad => rad * (180 / Math.PI);
          setPanoramaY([radToDeg(scenes[0].view().yaw()), radToDeg(scenes[1].view().yaw()), radToDeg(scenes[2].view().yaw())]);
        }, 25);

        // Preload images. Marzipano handles progressive rendering automatically.
        const imgsPromise = preloadImages([PanoramaMorning.src, PanoramaAfternoon.src, PanoramaEvening.src]);
        imgsPromise.then(() => {
          if (isMounted) {
            dispatch(handlepanoramaImgsLoading('load'));
            setIsLoading(true); // Images are ready
          }
        }).catch(error => {
          if (!isMounted) return;
          if (error.message === 'Image load timeout') {
            setLoadingError('Loading took too long. Please try again.');
          } else {
            dispatch(handlepanoramaImgsLoading('error'));
          }
        });
      } catch (err) {
        console.error(err);
        if (isMounted) setLoadingError('Failed to load panorama library');
      }
    }
    initPanorama();
    return () => {
      isMounted = false;
      clearInterval(intervalId);

      // Clean up Marzipano viewers
      viewersRef.current.forEach(viewer => {
        try {
          if (viewer) {
            viewer.destroy(); // Properly disposes of WebGL contexts and removes from DOM
          }
        } catch (e) {
          console.warn(e);
        }
      });
      viewersRef.current = [];
    };
  }, [openPanoramaModel, viewport]);
  useEffect(() => {
    if (openPanoramaModel) {
      setToggle(true);
      setTimeout(() => {
        setToggle(false);
      }, 400);
    }
  }, [openPanoramaModel]);
  return <>
      {/* Network Error UI */}
      {!isOnline && <div className={style.panoramaModal}>
          <div className={style.panoramaModalPlug}>
            <div className={style.panoramaModalClose} onClick={() => dispatch(handlePanoramaModel(false))} role='button' tabIndex='0'>
              <ButtonClose id='close_black' className={style.panoramaModalBtnClose} />
            </div>
            <p>Отсутствует соединение с сетью</p>
          </div>
        </div>}

      {loadingError && <div>{loadingError}</div>}
      {openPanoramaModel && pamoramasImgesLoading === 'load' && <LoaderCircle isLoading={isLoading} />}

      {/* Loading Error UI */}
      {openPanoramaModel && pamoramasImgesLoading === 'error' && isOnline && <div className={style.panoramaModal}>
          <div className={style.panoramaModalPlug}>
            <div className={style.panoramaModalClose} onClick={() => dispatch(handlePanoramaModel(false))} role='button' tabIndex='0'>
              <ButtonClose id='close_black' className={style.panoramaModalBtnClose} />
            </div>
            <p>При загрузке панорамы произошла ошибка. Попробуйте перезагрузить страницу</p>
          </div>
        </div>}

      {/* Main UI */}
      {openPanoramaModel && pamoramasImgesLoading === 'load' && <div className={style.panoramaModal}>
          <animated.div style={opacityStyle} className={style.panoramaModalWrapper}>
            <PanoramaCompass className={style.compass} panoramaY={panoramaY} />

            <div className={style.panoramaModalClose} onClick={() => dispatch(handlePanoramaModel(false))} role='button' tabIndex='0'>
              <ButtonClose id='close_black' className={style.panoramaModalBtnClose} />
            </div>

            <div className={style.panoramaModalContainer} ref={panoramaRef} />
            <div className={style.panoramaModalContainer_two} ref={panoramaRefTwo} />
            <div className={style.panoramaModalContainer_three} ref={panoramaRefThree} />

            {!isMobile && <NextImage fill src={Gradient_4.src} alt='gradient' className={style.panoramaMinBack} />}
            {isMobile && <NextImage fill src={Gradient_4.src} alt='gradient' className={style.panoramaMinBack} />}

            <div className={style.panoramaModalBtnsBox}>
              {viewport && forBtns && forBtns.map(item_0 => <button key={item_0.id} className={classNames(style.panoramaModalBtn, {
            [style.__active]: item_0.active
          })} onClick={() => changePic(item_0)}>
                    {item_0.title}
                  </button>)}
            </div>
          </animated.div>
        </div>}
    </>;
}
export default PanoramaModal;